import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Shuffle, 
  Copy, 
  Check, 
  Lock, 
  BookOpen, 
  Sparkles,
  ChevronDown,
  Quote,
  Lightbulb,
  PenTool,
  Crown
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { DECK_THEMES, PROMPT_CARDS, getAccessibleCards, getRandomCard, getCardsByTheme, getTodaysCard } from '../../data/promptDeck';

export default function PromptDeckPage() {
  const { user } = useAuth();
  const [currentCard, setCurrentCard] = useState(null);
  const [isFlipping, setIsFlipping] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState('all');
  const [showHistory, setShowHistory] = useState(false);
  const [drawnCards, setDrawnCards] = useState([]);
  const [showThemeDropdown, setShowThemeDropdown] = useState(false);

  // Determine user's tier based on membership
  const getUserTier = () => {
    if (!user) return 'free';
    // Check if user has correspondent/postmaster tier
    if (user.membership_tier === 'correspondent' || user.membership_tier === 'postmaster') {
      return 'full';
    }
    // Check if user has pen_friend tier
    if (user.membership_tier === 'pen_friend') {
      return 'basic';
    }
    return 'free';
  };

  const userTier = getUserTier();

  // Draw initial card on mount - show today's card first
  useEffect(() => {
    const todayCard = getTodaysCard(userTier);
    if (todayCard) {
      setCurrentCard(todayCard);
      setDrawnCards([todayCard]);
    } else {
      // Fallback to random card
      const randomCard = getRandomCard(userTier);
      if (randomCard) {
        setCurrentCard(randomCard);
        setDrawnCards([randomCard]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTier]);

  const drawNewCard = () => {
    setIsFlipping(true);
    
    setTimeout(() => {
      let newCard;
      if (selectedTheme === 'all') {
        newCard = getRandomCard(userTier, drawnCards.slice(-5).map(c => c.card));
      } else {
        const themeCards = getCardsByTheme(selectedTheme, userTier);
        const available = themeCards.filter(c => !drawnCards.slice(-5).map(dc => dc.card).includes(c.card));
        newCard = available.length > 0 
          ? available[Math.floor(Math.random() * available.length)]
          : themeCards[Math.floor(Math.random() * themeCards.length)];
      }
      
      if (newCard) {
        setCurrentCard(newCard);
        setDrawnCards(prev => [...prev.slice(-19), newCard]);
      }
      
      setTimeout(() => setIsFlipping(false), 300);
    }, 300);
  };

  const copyPrompt = () => {
    if (currentCard) {
      navigator.clipboard.writeText(currentCard.prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const selectTheme = (theme) => {
    setSelectedTheme(theme);
    setShowThemeDropdown(false);
    // Draw a new card from selected theme
    setTimeout(() => drawNewCard(), 100);
  };

  const getTierBadge = (tier) => {
    switch(tier) {
      case 'free': return null;
      case 'basic': return { label: 'Pen Friend', color: 'bg-emerald-100 text-emerald-700' };
      case 'full': return { label: 'Correspondent', color: 'bg-purple-100 text-purple-700' };
      default: return null;
    }
  };

  const isThemeLocked = (themeTier) => {
    const tierLevels = { free: 0, basic: 1, full: 2 };
    return tierLevels[themeTier] > tierLevels[userTier];
  };

  const accessibleCount = getAccessibleCards(userTier).length;
  const totalCount = PROMPT_CARDS.length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-lws-cream to-white" data-testid="prompt-deck-page">
      {/* Hero Section */}
      <section className="relative py-16 px-6 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-64 h-64 bg-lws-green rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-lws-gold rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-lws-gold font-medium">
              The Correspondence Ritual
            </p>
            <h1 className="text-4xl md:text-5xl font-serif text-lws-navy">
              365 Writing Prompts
            </h1>
            <p className="text-lg text-lws-taupe max-w-2xl mx-auto">
              Draw a card, find your words. Each prompt is paired with a historical letter 
              to inspire your own correspondence.
            </p>
            
            {/* Access indicator */}
            <div className="flex items-center justify-center gap-2 pt-4">
              <span className="text-sm text-lws-taupe">
                {accessibleCount} of {totalCount} prompts unlocked
              </span>
              {userTier !== 'full' && (
                <a href="#membership" className="text-sm text-lws-green hover:underline flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Unlock all
                </a>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Theme Filter */}
      <section className="px-6 pb-8">
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            <button
              onClick={() => setShowThemeDropdown(!showThemeDropdown)}
              className="w-full md:w-auto flex items-center justify-between gap-3 px-4 py-3 bg-white border border-stone-200 rounded-lg hover:border-lws-green transition-colors"
              data-testid="theme-selector"
            >
              <span className="text-sm text-stone-600">
                Theme: <span className="font-medium text-lws-navy">{selectedTheme === 'all' ? 'All Themes' : selectedTheme}</span>
              </span>
              <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${showThemeDropdown ? 'rotate-180' : ''}`} />
            </button>
            
            <AnimatePresence>
              {showThemeDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full left-0 mt-2 w-full md:w-80 bg-white border border-stone-200 rounded-lg shadow-lg z-20 max-h-80 overflow-y-auto"
                >
                  <button
                    onClick={() => selectTheme('all')}
                    className={`w-full px-4 py-3 text-left text-sm hover:bg-stone-50 flex items-center justify-between ${selectedTheme === 'all' ? 'bg-lws-cream' : ''}`}
                  >
                    <span>All Themes</span>
                    <span className="text-xs text-stone-400">{accessibleCount} prompts</span>
                  </button>
                  {DECK_THEMES.map((t) => {
                    const locked = isThemeLocked(t.tier);
                    const badge = getTierBadge(t.tier);
                    return (
                      <button
                        key={t.theme}
                        onClick={() => !locked && selectTheme(t.theme)}
                        disabled={locked}
                        className={`w-full px-4 py-3 text-left text-sm flex items-center justify-between gap-2 ${
                          locked ? 'opacity-50 cursor-not-allowed' : 'hover:bg-stone-50'
                        } ${selectedTheme === t.theme ? 'bg-lws-cream' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          {locked && <Lock className="w-3 h-3 text-stone-400" />}
                          <span>{t.theme}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {badge && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${badge.color}`}>
                              {badge.label}
                            </span>
                          )}
                          <span className="text-xs text-stone-400">{t.month}</span>
                        </div>
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* Card Display */}
      <section className="px-6 pb-16">
        <div className="max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            {currentCard && (
              <motion.div
                key={currentCard.card}
                initial={{ opacity: 0, rotateY: 90 }}
                animate={{ opacity: isFlipping ? 0 : 1, rotateY: isFlipping ? 90 : 0 }}
                exit={{ opacity: 0, rotateY: -90 }}
                transition={{ duration: 0.3 }}
                className="relative"
              >
                {/* Main Card */}
                <div className="bg-white rounded-2xl shadow-xl border border-stone-100 overflow-hidden">
                  {/* Card Header */}
                  <div className="bg-gradient-to-r from-lws-navy to-lws-navy/90 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                        <PenTool className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-white/70 text-xs uppercase tracking-wider">{currentCard.month} • Day {currentCard.day}</p>
                        <p className="text-white font-medium">{currentCard.theme}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-white/50 text-xs">Card</p>
                      <p className="text-white text-2xl font-serif">#{currentCard.card}</p>
                    </div>
                  </div>

                  {/* Prompt */}
                  <div className="p-8 md:p-10">
                    <p className="text-2xl md:text-3xl font-serif text-lws-navy leading-relaxed" data-testid="prompt-text">
                      {currentCard.prompt}
                    </p>
                  </div>

                  {/* Writing Guidance */}
                  <div className="px-8 pb-8 space-y-6">
                    {/* Approach */}
                    <div className="bg-lws-cream/50 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <Lightbulb className="w-4 h-4 text-lws-gold" />
                        <p className="text-xs uppercase tracking-wider text-lws-taupe font-medium">Approach</p>
                      </div>
                      <p className="text-sm text-stone-600">{currentCard.approach}</p>
                    </div>

                    {/* Starters */}
                    <div>
                      <p className="text-xs uppercase tracking-wider text-lws-taupe font-medium mb-3">Start with...</p>
                      <div className="flex flex-wrap gap-2">
                        {currentCard.starters.map((starter, i) => (
                          <span key={i} className="px-3 py-1.5 bg-stone-100 rounded-full text-sm text-stone-600 italic">
                            &ldquo;{starter}&rdquo;
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Tip */}
                    <div className="flex items-start gap-3 p-4 bg-lws-green/5 rounded-lg border border-lws-green/20">
                      <Sparkles className="w-4 h-4 text-lws-green mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-stone-600">{currentCard.tip}</p>
                    </div>
                  </div>

                  {/* Historical Letter */}
                  <div className="border-t border-stone-100 bg-stone-50 p-8">
                    <div className="flex items-center gap-2 mb-4">
                      <BookOpen className="w-4 h-4 text-lws-taupe" />
                      <p className="text-xs uppercase tracking-wider text-lws-taupe font-medium">From the Archive</p>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <p className="font-medium text-lws-navy">
                          {currentCard.letter.author} → {currentCard.letter.recipient}
                        </p>
                        <p className="text-sm text-stone-500">{currentCard.letter.year}</p>
                      </div>
                      
                      <p className="text-sm text-stone-600 italic">{currentCard.letter.context}</p>
                      
                      <blockquote className="border-l-2 border-lws-gold pl-4 py-2">
                        <Quote className="w-4 h-4 text-lws-gold/50 mb-2" />
                        <p className="text-stone-700 italic">{currentCard.letter.excerpt}</p>
                      </blockquote>
                      
                      <p className="text-sm text-lws-green font-medium">{currentCard.letter.tieBack}</p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
                  <Button
                    onClick={drawNewCard}
                    size="lg"
                    className="bg-lws-navy hover:bg-lws-navy/90 text-white px-8"
                    data-testid="draw-card-btn"
                  >
                    <Shuffle className="w-5 h-5 mr-2" />
                    Draw Another Prompt
                  </Button>
                  
                  <Button
                    onClick={copyPrompt}
                    variant="outline"
                    size="lg"
                    className="border-lws-green text-lws-green hover:bg-lws-green/5"
                    data-testid="copy-prompt-btn"
                  >
                    {copied ? (
                      <>
                        <Check className="w-5 h-5 mr-2" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-5 h-5 mr-2" />
                        Copy Prompt
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Upgrade CTA for non-full users */}
      {userTier !== 'full' && (
        <section className="px-6 pb-16">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-purple-50 to-lws-cream rounded-2xl p-8 md:p-10 border border-purple-100">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Crown className="w-8 h-8 text-purple-600" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-xl font-serif text-lws-navy mb-2">Unlock the Full Library</h3>
                  <p className="text-stone-600">
                    {userTier === 'free' 
                      ? `You have access to ${accessibleCount} free prompts. Upgrade to Pen Friend for ${getAccessibleCards('basic').length} prompts, or Correspondent for all ${totalCount}.`
                      : `You have access to ${accessibleCount} prompts. Upgrade to Correspondent for the complete ${totalCount}-prompt library.`
                    }
                  </p>
                </div>
                <a
                  href="#membership"
                  className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex-shrink-0"
                >
                  View Membership
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Recently Drawn */}
      {drawnCards.length > 1 && (
        <section className="px-6 pb-16">
          <div className="max-w-4xl mx-auto">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-2 text-sm text-stone-500 hover:text-lws-navy mb-4"
            >
              <ChevronDown className={`w-4 h-4 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
              Recently drawn ({drawnCards.length - 1})
            </button>
            
            <AnimatePresence>
              {showHistory && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                >
                  {drawnCards.slice(0, -1).reverse().map((card, i) => (
                    <motion.button
                      key={`${card.card}-${i}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      onClick={() => {
                        setCurrentCard(card);
                        setShowHistory(false);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="text-left p-4 bg-white rounded-lg border border-stone-200 hover:border-lws-green hover:shadow-md transition-all"
                    >
                      <p className="text-xs text-stone-400 mb-1">#{card.card} • {card.theme}</p>
                      <p className="text-sm text-stone-700 line-clamp-2">{card.prompt}</p>
                    </motion.button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>
      )}
    </div>
  );
}
